import React from 'react';
import { useNavigate } from 'react-router-dom';
import Background from '../../components/Background/Background';
import Button from '../../components/Button/Button';
import DetailSection from '../../components/DetailSection/DetailSection';
import Header from '../../components/Header/Header';
import useQuery from '../../hooks/useQuery';
import './_styles.results.css';
import { motion } from 'framer-motion';
import fadeIn from '../../animations/fadeIn';
import { useAppSelector } from '../../hooks/useApp';
import {
  getAirlineLogo,
  getHotelLogo,
  getPartnerLogo,
} from '../../data/partners';
import { formatNumber } from '../../utils/numberFunctions';

const Success = () => {
  const navigate = useNavigate();
  let query = useQuery();
  const { flights, hotels } = useAppSelector((state) => state.checks);
  const { isAnonymous } = useAppSelector((state) => state.auth);
  const type = query.get('type');

  const onLearnMoreClick = () => {
    navigate('/learnmore');
  };

  const onSignInClick = () => {
    navigate('/signin');
  };

  const getProgrammeMiles = (): string => {
    var total = 0;
    if (type === 'flight') {
      flights.successInfo?.forEach((flight) => {
        total += flight.convertedPoints ?? 0;
      });
    } else {
      hotels.successInfo?.forEach((flight) => {
        total += flight.convertedPoints ?? 0;
      });
    }
    return formatNumber(total);
  };

  const getProgrammeName = (): string => {
    if (type === 'flight') {
      return flights.successInfo?.[0].convertTo ?? '';
    } else {
      return hotels.successInfo?.[0].convertTo ?? '';
    }
  };

  return (
    <>
      {type === 'hotel' && (
        <div className='fixed left-0 right-0 top-0 bottom-0 bg-gradient-to-t from-[#002333] to-[#D1C1BA40] opacity-60 z-20 ' />
      )}
      {type === 'hotel' && (
        <img
          src='/hotels-desktop-background.png'
          className='results-hotel'
          alt='Hotel'
        />
      )}
      <Background
        image={type === 'flight' ? 'flight-results-full' : 'choose-page'}
      >
        <Header
          title={type === 'flight' ? 'Flight Results' : 'Hotel Results'}
        />
        <div className='results-container'>
          <motion.div
            className='border-2 border-primary aspect-square rounded-full w-[100px] flex justify-center items-center'
            variants={fadeIn(0.25)}
            initial='hidden'
            animate='enter'
            exit='exit'
          >
            <img src='/check.png' className='w-[50%]' alt='check' />
          </motion.div>
          <DetailSection
            title='destination'
            value={type === 'flight' ? flights.destination : hotels.destination}
          />
          <div className='flex justify-evenly items-center w-full'>
            <DetailSection
              title={type === 'flight' ? 'passengers' : 'guests'}
              value={
                type === 'flight'
                  ? flights.passengers?.toString()
                  : hotels.guests?.toString()
              }
              delay={0.15}
            />
            <DetailSection
              title={type === 'flight' ? 'class' : 'nights'}
              value={
                type === 'flight' ? flights.class : hotels.nights?.toString()
              }
              delay={0.15}
            />
          </div>
          <DetailSection title='You can use:'>
            {type === 'hotel' && (
              <div className='flex gap-4 justify-center items-center'>
                {hotels.successInfo?.map((item, index) => (
                  <React.Fragment key={item.program + item.points}>
                    <div className='use-partner'>
                      <div className='flex-center'>
                        <img
                          className='partner-logo'
                          src={getPartnerLogo(item.program)}
                          alt='Partner Logo'
                        />
                        <p>{formatNumber(item.points)} points.</p>
                      </div>
                    </div>
                    {hotels.successInfo &&
                      index < hotels.successInfo?.length - 1 && (
                        <p className='font-bold text-xl'>+</p>
                      )}
                  </React.Fragment>
                ))}
                {hotels.successInfo &&
                  hotels.successInfo.filter(
                    (item) => item.program === getProgrammeName()
                  ).length === 0 && (
                    <div className='convert'>
                      <img
                        src={'/icons/convert-icon.png'}
                        alt='convert points'
                        className='convert-icon'
                      />
                      <div className='flex-center'>
                        <img
                          className='partner-logo'
                          src={getPartnerLogo(getProgrammeName())}
                          alt='Partner Logo'
                        />
                        <p>
                          <>{getProgrammeMiles()} points.</>
                        </p>
                      </div>
                    </div>
                  )}
              </div>
            )}
            {type === 'flight' && (
              <div className='flex gap-4 justify-center items-center'>
                {flights.successInfo?.map((item, index) => (
                  <React.Fragment key={item.program + item.points}>
                    <div className='use-partner'>
                      <div className='flex-center'>
                        <img
                          className='partner-logo'
                          src={getPartnerLogo(item.program)}
                          alt='Partner Logo'
                        />
                        <p>{formatNumber(item.points)} {item.program.toLowerCase().includes("miles") ? "miles." : "points."}</p>
                      </div>
                    </div>
                    {flights.successInfo &&
                      index < flights.successInfo?.length - 1 && (
                        <p className='font-bold text-xl'>+</p>
                      )}
                  </React.Fragment>
                ))}
                {flights.successInfo &&
                  flights.successInfo.filter(
                    (item) => item.program === getProgrammeName()
                  ).length === 0 && (
                    <div className='convert'>
                      <img
                        src={'/icons/convert-icon.png'}
                        alt='convert points'
                        className='convert-icon'
                      />
                      <div className='flex-center'>
                        <img
                          className='partner-logo'
                          src={getAirlineLogo(flights.airline?.id ?? "")}
                          alt='Partner Logo'
                        />
                        <p>
                          <>{getProgrammeMiles()} miles.</>
                        </p>
                      </div>
                    </div>
                  )}
              </div>
            )}
          </DetailSection>
          <DetailSection
            title={type === 'hotel' ? 'to stay at:' : 'to fly with:'}
          >
            {type === 'hotel' ? (
              <div className='flex gap-4 justify-center items-center'>
                <div className='use-partner flex-center'>
                  <img
                    className='partner-logo'
                    src={getHotelLogo(hotels.hotel ? hotels.hotel.id : '')}
                    alt='partner logo'
                  />
                  <p>{hotels.hotel?.name}</p>
                </div>
              </div>
            ) : (
              <div className='flex gap-4 justify-center items-center'>
                <div className='use-partner flex-center'>
                  <img
                    className='partner-logo'
                    src={getAirlineLogo(
                      flights.airline ? flights.airline.id : ''
                    )}
                    alt='partner logo'
                  />
                  <p>{flights.airline?.name}</p>
                </div>
              </div>
            )}
          </DetailSection>
          <div className='flex flex-col gap-4 w-full'>
            <Button onClick={onLearnMoreClick} delay={0.5}>
              Learn More
            </Button>
            {isAnonymous && (
              <Button onClick={onSignInClick} type='secondary' delay={0.75}>
                Sign In or Sign Up
              </Button>
            )}
          </div>
        </div>
      </Background>
    </>
  );
};

export default Success;
